import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon } from "@iconify/react";
import { Input, type InputRef } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/shared/lib/utils";

/**
 * The header's "Search anything": a search box with a dropdown of grouped
 * results (pages and the portal's own data), keyboard support (↑ ↓ Enter
 * Esc) and Ctrl/⌘+K or "/" to jump into it. Each portal decides what's
 * searchable through `useSources`, which only runs while the dropdown is open,
 * so nothing is fetched or computed until someone actually searches.
 * Results from the page already open come first, under "On this page".
 */

export interface SearchItem {
  id: string;
  /** Section in the dropdown ("Pages", "Medications", …). */
  group: string;
  title: string;
  subtitle?: string;
  icon: string;
  /** Extra words that should find it but aren't shown (codes, synonyms). */
  keywords?: string;
  /** Where it goes: a route, or an action for anything a route can't express. */
  to: string | (() => void);
  /** The page it lives on, for "On this page" (defaults to the route's path). */
  page?: string;
  /** Found by a server search (e.g. words inside a note): shown even when the
   *  words aren't in its title, subtitle or keywords. */
  serverMatch?: boolean;
}

export interface SearchSources {
  items: SearchItem[];
  /** True while a portal's data (or a server search) is still loading. */
  loading?: boolean;
}

/** Called only while the dropdown is open; gets the (trimmed) query. */
export type UseSearchSources = (query: string) => SearchSources;

const PER_GROUP = 5;
const PAGES_GROUP = "Pages";

/** The page an item lives on: its own `page`, else its route without the query. */
function pageOf(item: SearchItem): string | null {
  if (item.page) return item.page;
  return typeof item.to === "string" ? item.to.split(/[?#]/)[0] : null;
}

/** Every word of the query must appear; the title matching first ranks higher. */
function score(item: SearchItem, words: string[]): number {
  const title = item.title.toLowerCase();
  const haystack = `${title} ${item.subtitle ?? ""} ${item.keywords ?? ""} ${item.group}`.toLowerCase();
  if (!words.every((w) => haystack.includes(w))) return item.serverMatch ? 0 : -1;
  const first = words[0];
  if (title.startsWith(first)) return 3;
  if (title.split(/\s+/).some((part) => part.startsWith(first))) return 2;
  if (title.includes(first)) return 1;
  return 0;
}

/** The matching part of a title in bold. */
function Highlight({ text, query }: { text: string; query: string }) {
  const word = query.trim().split(/\s+/)[0]?.toLowerCase() ?? "";
  const at = word ? text.toLowerCase().indexOf(word) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-sm bg-[#FEF6DC] p-0 text-inherit">{text.slice(at, at + word.length)}</mark>
      {text.slice(at + word.length)}
    </>
  );
}

function ResultsPanel({
  query, useSources, activeIndex, setActiveIndex, onPick, onResults,
}: {
  query: string;
  useSources: UseSearchSources;
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  onPick: (item: SearchItem) => void;
  onResults: (items: SearchItem[]) => void;
}) {
  const { items, loading } = useSources(query.trim());
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const here = useLocation().pathname;

  // Best matches first within each group; groups keep the portal's order,
  // except that the open page's groups come first.
  const groups = useMemo(() => {
    const byGroup = new Map<string, { item: SearchItem; s: number }[]>();
    for (const item of items) {
      const s = score(item, words);
      if (s < 0) continue;
      const list = byGroup.get(item.group) ?? [];
      list.push({ item, s });
      byGroup.set(item.group, list);
    }
    const all = [...byGroup].map(([group, list]) => ({
      group,
      total: list.length,
      onThisPage: group !== PAGES_GROUP && list.some(({ item }) => pageOf(item) === here),
      items: list.sort((a, b) => b.s - a.s).slice(0, PER_GROUP).map((x) => x.item),
    }));
    return [...all.filter((g) => g.onThisPage), ...all.filter((g) => !g.onThisPage)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, words.join(" "), here]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  useEffect(() => onResults(flat), [flat, onResults]);

  if (flat.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-[#6B7280]">
        {loading ? (
          <span className="inline-flex items-center gap-2"><Icon icon="lucide:loader-2" className="h-4 w-4 animate-spin" /> Searching…</span>
        ) : (
          <>No results for “<span className="font-semibold text-[#0A2947]">{query.trim()}</span>”</>
        )}
      </div>
    );
  }

  let index = -1;
  return (
    <div role="listbox" aria-label="Search results" className="max-h-[min(70vh,520px)] overflow-y-auto py-2">
      {groups.map((group) => (
        <div key={group.group} className="px-2 pb-1">
          <p className="!mb-0 px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
            {group.onThisPage && <span className="text-[#E4002B]">On this page · </span>}
            {group.group}
            {group.total > group.items.length && <span className="normal-case tracking-normal"> · top {group.items.length} of {group.total}</span>}
          </p>
          {group.items.map((item) => {
            index += 1;
            const i = index;
            const active = i === activeIndex;
            return (
              <button
                key={`${item.group}:${item.id}`}
                id={`global-search-option-${i}`}
                type="button"
                role="option"
                aria-selected={active}
                onMouseEnter={() => setActiveIndex(i)}
                // mousedown, not click: picks before the input's blur closes the panel.
                onMouseDown={(event) => { event.preventDefault(); onPick(item); }}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left outline-none transition-colors",
                  active ? "bg-[#FFF2F4]" : "hover:bg-[#F9FAFB]",
                )}
              >
                <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", active ? "bg-[#E4002B]/10 text-[#E4002B]" : "bg-[#F3F4F6] text-[#4B5563]")}>
                  <Icon icon={item.icon} className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-[#0A2947]"><Highlight text={item.title} query={query} /></span>
                  {item.subtitle && <span className="block truncate text-xs text-[#6B7280]">{item.subtitle}</span>}
                </span>
                {active && <Icon icon="lucide:corner-down-left" className="h-3.5 w-3.5 shrink-0 text-[#9CA3AF]" />}
              </button>
            );
          })}
        </div>
      ))}
      {loading && (
        <p className="!mb-0 flex items-center gap-2 px-4 py-2 text-xs text-[#9CA3AF]">
          <Icon icon="lucide:loader-2" className="h-3.5 w-3.5 animate-spin" /> Looking for more…
        </p>
      )}
    </div>
  );
}

export function GlobalSearch({
  useSources, placeholder = "Search anything...", className,
}: {
  useSources: UseSearchSources;
  placeholder?: string;
  /** Classes for the input (the header's HEADER_SEARCH_CLASS). */
  className?: string;
}) {
  const navigate = useNavigate();
  const inputRef = useRef<InputRef>(null);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const resultsRef = useRef<SearchItem[]>([]);
  const open = focused && query.trim().length > 0;

  // Ctrl/⌘+K anywhere, or "/" when not typing in a field, jumps to the search.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if ((event.key === "k" && (event.ctrlKey || event.metaKey)) || (event.key === "/" && !typing)) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => setActiveIndex(0), [query]);

  const pick = (item: SearchItem) => {
    if (typeof item.to === "string") navigate(item.to);
    else item.to();
    setQuery("");
    inputRef.current?.blur();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const count = resultsRef.current.length;
    if (event.key === "Escape") {
      setQuery("");
      inputRef.current?.blur();
    } else if (event.key === "ArrowDown" && count) {
      event.preventDefault();
      setActiveIndex((activeIndex + 1) % count);
    } else if (event.key === "ArrowUp" && count) {
      event.preventDefault();
      setActiveIndex((activeIndex - 1 + count) % count);
    } else if (event.key === "Enter" && count) {
      event.preventDefault();
      pick(resultsRef.current[Math.min(activeIndex, count - 1)]);
    }
  };

  // Keep the highlighted option in view while moving with the arrows.
  useEffect(() => {
    if (open) document.getElementById(`global-search-option-${activeIndex}`)?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  const shortcut: ReactNode = (
    <kbd className="hidden rounded border border-[#E5E7EB] bg-[#F9FAFB] px-1.5 font-sans text-[10px] font-semibold text-[#9CA3AF] xl:inline">Ctrl K</kbd>
  );

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        role="combobox"
        aria-expanded={open}
        aria-controls="global-search-results"
        aria-activedescendant={open ? `global-search-option-${activeIndex}` : undefined}
        aria-label="Search"
        prefix={<Icon icon="lucide:search" className="h-4 w-4 text-[#4B5563]" />}
        suffix={query ? (
          <button type="button" aria-label="Clear search" onMouseDown={(e) => { e.preventDefault(); setQuery(""); }} className="flex text-[#9CA3AF] hover:text-[#4B5563]">
            <Icon icon="lucide:x" className="h-3.5 w-3.5" />
          </button>
        ) : shortcut}
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={onKeyDown}
        className={className}
      />
      {open && (
        <div
          id="global-search-results"
          className="absolute right-0 top-full z-[1200] mt-2 w-[min(440px,calc(100vw-32px))] overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_16px_40px_rgba(17,24,39,0.16)]"
        >
          <ResultsPanel
            query={query}
            useSources={useSources}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            onPick={pick}
            onResults={(items) => { resultsRef.current = items; }}
          />
          <p className="!mb-0 flex items-center gap-3 border-t border-[#F0F1F3] bg-[#F9FAFB] px-4 py-2 text-[11px] text-[#9CA3AF]">
            <span>↑ ↓ to move</span><span>Enter to open</span><span>Esc to close</span>
          </p>
        </div>
      )}
    </div>
  );
}
